import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TextInput,
  Button,
  Container,
  Alert,
  Anchor,
  Title,
  Stack,
  Image,
  Box,
  Paper,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuthStore } from '../../../application/store/authStore';
import { ForgotPasswordCommand } from '../../../application/dto/auth/ForgotPasswordCommand';
import { PUBLIC_ROUTES } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

/**
 * ForgotPasswordPage — MineMiner SPA password-reset request screen.
 *
 * sprint-107 S1.5 (epic-013 Sprint D — Stream A item A2; brand-architecture
 * hotfix). Rep-facing surface — branding matches LoginPage exactly
 * (MineMiner logo, "your MineMiner account" copy). Per memory
 * `project_mineminer_brand_architecture`: mymineminer.com is our
 * customer-facing SaaS; my.syncminesafety.com is for one client's
 * prospects only — never mix audiences.
 *
 * Posts to `POST /api/mineminer/auth/forgot-password` via the authStore.
 * Bot-protection fields (`honeypot` + `formLoadTimestamp`) are carried
 * verbatim from the core MentionedMe page so the backend handler can
 * detect bot activity without surfacing detection to the UI (always
 * returns 200 success copy).
 *
 * The honeypot input lives inside a Box that's positioned off-screen via
 * Mantine props rather than the forbidden `style=` component prop (memory
 * `feedback_frontend_style_prop_lint`).
 */
export const ForgotPasswordPage: React.FC = (): React.ReactElement => {
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const passwordResetState = useAuthStore((state) => state.passwordResetState);
  const passwordResetError = useAuthStore((state) => state.passwordResetError);

  // Capture form-load timestamp for bot detection; fast submissions (<3s) are
  // flagged by the backend handler.
  const [formLoadTime] = useState<number>(() => Date.now());

  const form = useForm<ForgotPasswordCommand>({
    initialValues: {
      email: '',
      honeypot: '',
    },
    validate: {
      email: (value: string): string | null => {
        if (!value || value.trim().length === 0) {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return 'Please enter a valid email address';
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values: ForgotPasswordCommand): Promise<void> => {
    const commandWithTimestamp: ForgotPasswordCommand = {
      ...values,
      formLoadTimestamp: formLoadTime,
    };

    try {
      await forgotPassword(commandWithTimestamp);
      form.reset();
    } catch {
      // Error state is captured in the auth store; the Alert below renders it.
    }
  };

  return (
    <Box mih="100vh" bg="gray.0" py={80} px="md">
      <Container size="xs">
        <Paper bg="white" p={48} radius="md" shadow="sm" withBorder>
          <Stack gap="md" align="center" mb="md">
            <Image src={LogoSvg} alt="MineMiner" h={48} w="auto" />
            <Title order={1} size="h2" ta="center">
              Forgot your password?
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Enter your MineMiner email and we&rsquo;ll send a reset link.
            </Text>
          </Stack>

          {passwordResetState === 'error' && passwordResetError !== null && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              mb="md"
            >
              {passwordResetError}
            </Alert>
          )}

          {passwordResetState === 'emailSent' && (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              variant="light"
              mb="md"
              title="Check your inbox"
            >
              If an account exists with that email, you&rsquo;ll receive a
              password-reset link shortly. The link expires in 1 hour.
            </Alert>
          )}

          {passwordResetState !== 'emailSent' && (
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  size="md"
                  {...form.getInputProps('email')}
                />

                {/* Honeypot — off-screen via Mantine layout props (pos/left/h/w/opacity)
                    rather than the forbidden `style=` component prop. The TextInput
                    itself stays hidden via tabIndex=-1 + aria-hidden. */}
                <Box
                  pos="absolute"
                  left={-9999}
                  top={-9999}
                  h={0}
                  w={0}
                  opacity={0}
                  aria-hidden="true"
                >
                  <TextInput
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.values.honeypot ?? ''}
                    onChange={(e): void =>
                      form.setFieldValue('honeypot', e.currentTarget.value)
                    }
                  />
                </Box>

                <Button
                  type="submit"
                  size="lg"
                  variant="filled"
                  color="brand"
                  disabled={!form.isValid()}
                  loading={passwordResetState === 'requesting'}
                  fullWidth
                  mt="sm"
                >
                  Send reset link
                </Button>
              </Stack>
            </form>
          )}

          <Text ta="center" size="sm" mt="md">
            <Anchor component={Link} to={PUBLIC_ROUTES.LOGIN} fw={600}>
              Back to sign in
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Box>
  );
};
